import { EmptyStateNoResults, ExternalLink, InlineEmptyStateOnboarding, Pagination, SearchInput, SimpleDataTable, formatExternalUrl } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ResourceCollection, OctopusPackageVersionBuildInformationMappedResource, Repository } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { useCallback } from "react";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
interface BuildInformationRepositoryPageProps {
    spaceId: string;
    loaderData: BuildInformationPageLoaderData;
    queryParams: BuildInformationRepositoryPageQueryParams;
    setQueryParams: QueryParamValuesSetter<BuildInformationRepositoryPageQueryParams>;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export interface BuildInformationRepositoryPageQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    partialPackageId: string;
}
export async function buildInformationRepositoryPageLoader(repository: Repository, queryParams: BuildInformationRepositoryPageQueryParams): Promise<BuildInformationPageLoaderData> {
    const packages = getPaginatedBuildInformation(repository, queryParams.page, queryParams.pageSize, queryParams.partialPackageId);
    const totalPackages = getTotalPackages(repository);
    return {
        packages: await packages,
        totalPackages: await totalPackages,
    };
}
interface BuildInformationPageLoaderData {
    packages: ResourceCollection<OctopusPackageVersionBuildInformationMappedResource>;
    totalPackages: number;
}
const pageTitle = "Build Information Repository";
export function BuildInformationRepositoryPage({ spaceId, loaderData, queryParams, setQueryParams }: BuildInformationRepositoryPageProps) {
    const { result: packages } = usePaginatedBuildInformation(queryParams, loaderData);
    const [debouncedFilter, setDebouncedFilter] = useDebouncedExternalState(queryParams.partialPackageId, (filter) => setQueryParams((prev) => ({ ...prev, partialPackageId: filter, page: 1 })), 200);
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    const filterInputs = [<SearchInput autoFocus={true} value={debouncedFilter} onChange={setDebouncedFilter} accessibleName={"Search build information by name"} placeholder={"Search by name"}/>];
    const pagination = (<Pagination label={"Items"} totalResults={packages.TotalResults} itemsPerPageOptions={pageSizeOptions} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} selectedItemsPerPage={packages.ItemsPerPage}/>);
    if (loaderData.totalPackages === 0) {
        return (<PageContent header={{ title: pageTitle }} filters={{ inputs: filterInputs }} pagination={{ ui: pagination, placement: "top" }}>
                <BorderedContent>
                    <InlineEmptyStateOnboarding title={"No build information is currently available."} description={"Once your CI tool is configured to provide build information, it will appear here."} actions={{
                external: {
                    onClick: () => {
                        window.open(formatExternalUrl("BuildInformation"), "_blank");
                    },
                    label: "View Supported Integrations",
                },
            }} illustration={"EmptyState"}/>
                </BorderedContent>
            </PageContent>);
    }
    const hideBottomPagination = packages.Items.length < 20;
    return (<PageContent header={{ title: pageTitle }} filters={{ inputs: filterInputs }} pagination={{
            ui: pagination,
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }}>
            <BorderedContent>
                {packages.TotalResults === 0 ? (<EmptyStateNoResults />) : (<SimpleDataTable data={packages.Items} columns={[
                { title: "Package ID", render: (pkg) => <InternalLink to={links.buildInformationVersionsPage.generateUrl({ spaceId, packageId: pkg.PackageId })}>{pkg.PackageId}</InternalLink> },
                { title: "Highest version", render: (pkg) => <InternalLink to={links.buildInformationDetailsPage.generateUrl({ spaceId, id: pkg.Id })}>{pkg.Version}</InternalLink> },
                { title: "Build Environment", render: (pkg) => pkg.BuildEnvironment },
                { title: "Branch", render: (pkg) => pkg.Branch },
                { title: "Build", render: (pkg) => (pkg.BuildUrl ? <ExternalLink href={pkg.BuildUrl}>{pkg.BuildNumber}</ExternalLink> : null) },
            ]} getRowKey={(pkg) => pkg.Id}/>)}
            </BorderedContent>
        </PageContent>);
}
function usePaginatedBuildInformation(queryParams: BuildInformationRepositoryPageQueryParams, loaderData: BuildInformationPageLoaderData) {
    return useQuery((repository) => getPaginatedBuildInformation(repository, queryParams.page, queryParams.pageSize, queryParams.partialPackageId), [queryParams.page, queryParams.pageSize, queryParams.partialPackageId], "Get paginated build information", {
        initialResult: loaderData.packages,
    });
}
function getPaginatedBuildInformation(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, partialPackageId: string = "") {
    return repository.BuildInformationRepository.list({
        skip: (page - 1) * pageSize,
        take: pageSize,
        partialPackageId,
    });
}
async function getTotalPackages(repository: Repository) {
    const result = await repository.BuildInformationRepository.list({ skip: 0, take: 0 });
    return result.TotalResults;
}
